import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  CHANNEL_TOKEN_LENGTH,
  USER_TOKEN_LENGTH
} from '../components/utilsDesign';
import { fetchUsers } from '../components/utilsSocial';
import { ChatStateContext } from './chatStateContext';
import { API_URL } from '../apiUrl';

const MessageContext = React.createContext();



export const HD_CAFE_CHAT = {
  user: {
    name: 'HD Cafe Support',
    image: 'https://cdn.prod.website-files.com/643f3bd2d71a69bb42b11dfb/648d172ec0fd1cc0a59e59cc_fav.png',
  },
  avatar: {
    token: 'hd_cafe',
    image: 'https://cdn.prod.website-files.com/643f3bd2d71a69bb42b11dfb/648d172ec0fd1cc0a59e59cc_fav.png',
    name: 'HD Cafe Support'
  }
}

const MessageProvider = React.memo(({ children }) => {
  const { currentChat, setCurrentChat } = useContext(ChatStateContext);

  const updateTimeoutRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const lastUpdateTimeRef = useRef(0);
  const chatsInFlightRef = useRef(new Set());
  const membersInFlightRef = useRef(new Set());
  const membersDataRef = useRef();
  const cachedChatsRef = useRef({});
  const CHAT_FETCH_CONCURRENCY = 3;
  const USER_FETCH_BATCH = 6;
  
  const performanceRef = useRef({
    updateCount: 0,
    lastUpdateDuration: 0,
    averageUpdateTime: 0
  });

  const [unauthorized, setUnauthorized] = useState(false)

  const [chatsList, setChatList] = useState()
  const [unread, setUnread] = useState()
  const [cachedChats, setCachedChats] = useState({})

  useEffect(() => { cachedChatsRef.current = cachedChats; }, [cachedChats]);

  const afterSendCallback = useCallback((newMessage) => {
    setCachedChats(prev => {
      const newChannelMessages = {}
      const prevMessages = prev[newMessage.channel] ? prev[newMessage.channel] : {}
      newChannelMessages[newMessage.channel] = [
        ...prevMessages,
        newMessage
      ]

      return ({
        ...prev,
        ...newChannelMessages
      })

    })
  }, [])

  const sendMessage = useCallback((to, message, lastMessageId) => {
    _send(to, message, null, lastMessageId)
  }, [])

  const _send = async (to = '', text = '', callback = () => void (0), lastMessageId) => {
    const url = `${API_URL}/api/message/send`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: to,
          text: text
        })
      });

      if (response.ok) {
        const data = await response.json();
        // console.log('Success:', data);

        const channel = data.channel;
        fetchChatsMessages(lastMessageId ? [`${channel}/${lastMessageId}`] : [channel])
        callback && callback(data);
      } else {
        console.log('Error: ', response.status);
      }
    } catch (error) {
      console.error('Error:', error.message);
    }

  };

  const [membersData, setMembersData] = useState()
  useEffect(() => { membersDataRef.current = membersData; }, [membersData]);

  const fetchChatsMessages = useCallback((chatsIds = [], callback = () => void (0)) => {
    if (!chatsIds || !chatsIds.length) {
      callback({});
      return;
    }

    const cache = cachedChatsRef.current || {};
    const chatsToFetch = chatsIds.filter(chatId => {
      const id = String(chatId).split('/')[0];
      const isDelta = String(chatId).includes('/');
      if (isDelta) return !chatsInFlightRef.current.has(chatId);
      return !cache[id] && !chatsInFlightRef.current.has(id);
    });

    if (chatsToFetch.length === 0) {
      const cachedData = chatsIds.reduce((acc, chatId) => {
        const id = String(chatId).split('/')[0];
        if (cache[id]) acc[id] = cache[id];
        return acc;
      }, {});
      callback(cachedData);
      return;
    }

    chatsToFetch.forEach(chatId => {
      chatsInFlightRef.current.add(chatId);
      chatsInFlightRef.current.add(String(chatId).split('/')[0]);
    });

    const chatsData = {};
    let cursor = 0;
    let running = 0;
    let settled = 0;

    const finishOne = () => {
      settled += 1;
      if (settled >= chatsToFetch.length) {
        const allChatsData = { ...chatsData };
        chatsIds.forEach(chatId => {
          const id = String(chatId).split('/')[0];
          if (cachedChatsRef.current[id] && !allChatsData[id]) {
            allChatsData[id] = cachedChatsRef.current[id];
          }
        });
        callback(allChatsData);
      } else {
        pump();
      }
    };

    const pump = () => {
      while (running < CHAT_FETCH_CONCURRENCY && cursor < chatsToFetch.length) {
        const chatId = chatsToFetch[cursor++];
        const id = String(chatId).split('/')[0];
        running += 1;
        _getChatMessages(
          chatId,
          chatData => {
            chatsData[id] = chatData;
            setCachedChats(prev => ({ ...prev, [id]: chatData }));
            chatsInFlightRef.current.delete(id);
            chatsInFlightRef.current.delete(chatId);
            running -= 1;
            finishOne();
          },
          error => {
            console.error(`Error fetching data for chat ${chatId}:`, error);
            chatsInFlightRef.current.delete(id);
            chatsInFlightRef.current.delete(chatId);
            running -= 1;
            finishOne();
          }
        );
      }
    };

    pump();
  }, []);




  const getAllMembersData = (chatsList) => {
    let membersList = [];
    if (Array.isArray(chatsList) && chatsList.length) {
      chatsList.forEach(one => {
        if (Array.isArray(one.members)) membersList.push(...one.members);
      });
      membersList = [...new Set(membersList)]
    }
    membersList = membersList.filter(one => one !== 'hd_cafe')
    return membersList;
  }


  const checkIfAllMembersDataLoaded = useCallback((list = []) => {
    if (!list.length) return true;
    const have = membersDataRef.current || membersData;
    if (!have || !Object.keys(have).length) return false;
    return list.every(id => have[id]);
  }, [membersData])

  const fetchUsersData = useCallback((membersList, priority = false) => {
    const have = membersDataRef.current || {};
    const missing = [...new Set(membersList || [])].filter(id =>
      id && id !== 'hd_cafe' && !have[id] && !membersInFlightRef.current.has(id)
    );
    if (!missing.length) return;

    missing.forEach(id => membersInFlightRef.current.add(id));

    const apply = (ids, newMembersData) => {
      ids.forEach(id => membersInFlightRef.current.delete(id));
      if (newMembersData && Object.keys(newMembersData).length) {
        setMembersData(prev => ({ ...(prev || {}), ...newMembersData }));
      }
    };

    if (priority || missing.length <= USER_FETCH_BATCH) {
      fetchUsers(missing, (data) => apply(missing, data), () => {
        missing.forEach(id => membersInFlightRef.current.delete(id));
      });
      return;
    }

    let i = 0;
    const next = () => {
      if (i >= missing.length) return;
      const slice = missing.slice(i, i + USER_FETCH_BATCH);
      i += USER_FETCH_BATCH;
      fetchUsers(slice, (data) => {
        apply(slice, data);
        next();
      }, () => {
        slice.forEach(id => membersInFlightRef.current.delete(id));
        next();
      });
    };
    next();
  }, [])



  const checkExistingChat = useCallback((to) => {
    if (!chatsList || !chatsList.length) return false
    const ex = chatsList.filter(one => one.members.includes(to));
    return ex.length ? ex[0] : false
  }, [chatsList])


  const findChannelByMember = useCallback((to) => {
    if (!chatsList || !chatsList.length) return null
    return checkExistingChat(to) ? chatsList.filter(one => one.members.includes(to))[0]._id : null;
  }, [chatsList, checkExistingChat])

  const startChat = async (to = '', text = '', callback = () => void (0)) => {

    const afterChatCallback = (newMessage) => {
      if (checkExistingChat(to)) {
        return findChannelByMember(to)
      } else {
        _getAllChats(allChats => {
          const list = Array.isArray(allChats) ? [...allChats].reverse() : []
          setChatList(list)
          fetchChatsMessages([newMessage.channel])
          // callback(allChats.length ? allChats[allChats.length - 1]._id : null)
          callback(newMessage.channel)
        })
      }
    }

    _send(to, text, afterChatCallback)
  };


  const _getAllChats = (callback = () => void (0), callbackError = () => void (0)) => {
    const fetchData = () => {
      return fetch(`${API_URL}/api/message/channels`)
        .then(response => {
          if (!response.ok) {
            // throw new Error('Network response was not ok');
          }
          return response.json();
        });
    };

    fetchData()
      .then(chats => {
        if (chats.status !== 404) {
          setUnauthorized(false)
          // console.log('chats = ', chats);
          callback(chats);
        } else {
          callbackError();
        }
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        callbackError(error);
        setUnauthorized(true)
      });
  }


  const getChannel = useCallback((chatId, callback = () => void (0)) => {
    if (!Array.isArray(chatsList) || !chatId) {
      callback({})
      return
    }
    const chat = chatsList.filter(one => String(one._id) === String(chatId));
    chat.length ? callback(chat[0]) : callback({})
  }, [chatsList])


  const getChatMessages = useCallback((chatId, callback = () => void (0), force) => {
    return cachedChats[chatId] ? cachedChats[chatId] : false;
    // if (!force) {
    //   cachedChats[chatId] ? callback(cachedChats[chatId]) : _getChatMessages(chatId, callback)
    // } else {
    //   _getChatMessages(chatId, callback)
    // }
  }, [cachedChats])

  const _getChatMessages = (chatId, callback = () => void (0), callbackError = () => void (0)) => {
    const fetchData = (chatId) => {
      return fetch(`${API_URL}/api/message/messages/${chatId}`)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        });
    };

    fetchData(chatId)
      .then(chatData => {
        if (chatData.status !== 404) {
          setUnauthorized(false)
          callback(chatData);
        } else {
          callbackError(chatData);
        }
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        callbackError(error);
        setUnauthorized(true)
      });
  };

  useEffect(() => {
    if (!currentChat) return;

    if (currentChat.length === CHANNEL_TOKEN_LENGTH && !cachedChatsRef.current[currentChat]) {
      fetchChatsMessages([currentChat]);
    }

    if (currentChat.length === USER_TOKEN_LENGTH) {
      if (checkExistingChat(currentChat)) {
        setCurrentChat(checkExistingChat(currentChat)._id)
      } else {
        fetchUsersData([currentChat], true)
      }
    }
  }, [currentChat, checkExistingChat, fetchUsersData, fetchChatsMessages, setCurrentChat])

  // Open chat first: its counterpart, then the rest of the list in the background.
  useEffect(() => {
    const priority = [];
    if (currentChat && Array.isArray(chatsList)) {
      const channel = chatsList.find(one => String(one._id) === String(currentChat));
      if (channel?.members) priority.push(...channel.members);
    }
    const cached = currentChat && cachedChats[currentChat];
    if (Array.isArray(cached)) {
      cached.forEach(m => { if (m?.from) priority.push(m.from); });
    }
    const priorityIds = [...new Set(priority)].filter(id => id && id !== 'hd_cafe');
    if (priorityIds.length) fetchUsersData(priorityIds, true);

    const rest = getAllMembersData(chatsList).filter(id => !priorityIds.includes(id));
    if (rest.length) fetchUsersData(rest);
  }, [chatsList, cachedChats, currentChat, fetchUsersData])


  const _getUnreadCount = (callback = () => void (0), callbackError = () => void (0)) => {

    if (unauthorized) return callback([]);

    const fetchData = () => {
      return fetch(`${API_URL}/api/message/count`)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        });
    };

    fetchData()
      .then(newMessages => {
        if (newMessages && !newMessages.status) {
          // console.log('newMessages = ', newMessages);
          setUnauthorized(false)
          setUnread(newMessages)
          callback(newMessages);
        } else if (newMessages && newMessages.status !== 404) {
          setUnauthorized(false)
          setUnread(newMessages)
          callback(newMessages);
        } else {
          callbackError(newMessages);
        }
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        callbackError(error);
        setUnauthorized(true)
      });
  };

  const getUnread = useCallback((callback = () => void (0), callbackError = () => void (0)) => {
    _getUnreadCount(callback, callbackError)
  }, []);

  const getUnreadFor = useCallback((chatId, callback = () => void (0), force) => {
    const filterNewByChat = (chatId, newMessages = []) => {
      const newM = newMessages.filter(one => one._id === chatId);
      const count = newM.length ? newM[0].count : 0;
      return count;
    }
    if (force || unread == null) {
      _getUnreadCount((newMessages) => {
        callback(filterNewByChat(chatId, newMessages))
      })
    } else {
      callback(filterNewByChat(chatId, unread))
    }
  }, [unread]);





  useEffect(() => {
    getUnread((un) => setUnread(un))
    // _getAllChats(data => setChatList(data))
  }, [])



  useEffect(() => {
    if (unauthorized) return;
    if (!chatsList) {
      _getAllChats(chats => {
        setChatList(Array.isArray(chats) ? chats : [])
      })
    }
  }, [unauthorized])

  // Only the open chat and unread threads — full history for every channel is too slow.
  useEffect(() => {
    if (!currentChat && !(Array.isArray(unread) && unread.length)) return;
    const unreadIds = Array.isArray(unread) ? unread.map(one => one._id) : [];
    const queue = [currentChat, ...unreadIds].filter(Boolean);
    if (queue.length) fetchChatsMessages(queue);
  }, [unread, currentChat, fetchChatsMessages])





  // Состояние для индикатора обновления
  const [updating, setUpdating] = useState(false);

  // Функция для автообновления чатов с дебаунсингом
  const autoUpdateChats = useCallback(() => {
    if (unauthorized || isUpdatingRef.current) {
      return;
    }

    const now = Date.now();
    // Минимальный интервал между обновлениями - 3 секунды
    if (now - lastUpdateTimeRef.current < 3000) {
      return;
    }

    const startTime = performance.now();
    isUpdatingRef.current = true;
    lastUpdateTimeRef.current = now;
    setUpdating(true);
    
    // Обновляем непрочитанные сообщения
    getUnread((newUnread) => {
      // Обновляем состояние unread только если данные изменились
      setUnread(prevUnread => {
        if (JSON.stringify(prevUnread) !== JSON.stringify(newUnread)) {
          return newUnread;
        }
        return prevUnread;
      });
      
      // Если есть новые сообщения, обновляем список чатов
      if (newUnread && newUnread.length > 0) {
        _getAllChats((allChats) => {
          setChatList(prevChats => {
            const newChats = Array.isArray(allChats) ? [...allChats].reverse() : [];
            // Обновляем только если список изменился
            if (JSON.stringify(prevChats) !== JSON.stringify(newChats)) {
              return newChats;
            }
            return prevChats;
          });
          // Обновляем сообщения для чатов с новыми сообщениями
          fetchChatsMessages([...newUnread.map(one => one._id)]);
        });
      }
      
      // Скрываем индикатор загрузки и обновляем метрики производительности
      setTimeout(() => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        // Обновляем метрики производительности
        performanceRef.current.updateCount += 1;
        performanceRef.current.lastUpdateDuration = duration;
        performanceRef.current.averageUpdateTime = 
          (performanceRef.current.averageUpdateTime * (performanceRef.current.updateCount - 1) + duration) / 
          performanceRef.current.updateCount;
        
        // Логируем производительность в консоль для отладки
        if (process.env.NODE_ENV === 'development') {
          console.log(`MessageContext update #${performanceRef.current.updateCount}: ${duration.toFixed(2)}ms (avg: ${performanceRef.current.averageUpdateTime.toFixed(2)}ms)`);
        }
        
        setUpdating(false);
        isUpdatingRef.current = false;
      }, 500); // Уменьшили время до 500мс
    });
  }, [unauthorized, fetchChatsMessages]);

  // Дебаунсированная функция автообновления
  const debouncedAutoUpdate = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      autoUpdateChats();
    }, 1000); // Дебаунсинг 1 секунда
  }, [autoUpdateChats]);

  // Автообновление каждые 10 секунд (увеличили интервал)
  useEffect(() => {
    let intervalId;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Если страница скрыта, останавливаем автообновление
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        // Если страница видима, запускаем автообновление
        if (!intervalId) {
          intervalId = setInterval(debouncedAutoUpdate, 10000);
        }
      }
    };
    
    // Запускаем автообновление только если страница видима
    if (!document.hidden) {
      intervalId = setInterval(debouncedAutoUpdate, 10000);
    }
    
    // Слушаем изменения видимости страницы
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [debouncedAutoUpdate]);






  const _markRead = useCallback(async (chatId = '', callback = () => void (0)) => {
    const url = `${API_URL}/api/message/read-all/`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel: chatId
        })
      });
      if (response.ok) {
        const data = await response.json();
        // console.log('Marked read:', chatId, data);
        _getChatMessages(chatId, (chatData) => {
          if (chatData && chatData.status === 404) return;
          setCachedChats(prev => ({
            ...prev,
            [chatId]: chatData
          }))
        })
        
        // Обновляем состояние unread после пометки как прочитанное
        getUnread((newUnread) => {
          setUnread(newUnread);
        });
        
        callback && callback(data);
      }
    } catch (error) {
      console.error('Error mark readr:', chatId, error.message);
    }
  }, [getUnread]);



  const getMemberData = useCallback((userId) => {
    return membersData && membersData[userId] ? membersData[userId] : null
  }, [membersData])

  // Функция для получения метрик производительности
  const getPerformanceMetrics = useCallback(() => {
    return {
      ...performanceRef.current,
      isUpdating: isUpdatingRef.current,
      timeSinceLastUpdate: Date.now() - lastUpdateTimeRef.current
    };
  }, [])


  const value = useMemo(() => ({
    unauthorized,
    updating,
    chatsList,
    unread,
    membersData,
    cachedChats,
  }), [unauthorized, updating, chatsList, unread, membersData, cachedChats])


  // console.log('unread = ', unauthorized);


  return (
    <MessageContext.Provider
      value={{
        ...value,
        getChannel: getChannel,
        getChatMessages: getChatMessages,
        sendMessage: sendMessage,
        startChat: startChat,
        findChannelByMember: findChannelByMember,
        fetchChatsMessages: fetchChatsMessages,
        getUnreadFor: getUnreadFor,
        getUnread: getUnread,
        markRead: _markRead,
        setCurrentChat: setCurrentChat,
        getMemberData: getMemberData,
        fetchUsersData: fetchUsersData,
        // getPerformanceMetrics: getPerformanceMetrics
      }}
    >
      {children}
    </MessageContext.Provider>
  )

})

const ContextConsumer = MessageContext.Consumer;

export { ContextConsumer, MessageContext, MessageProvider };
